import { Box, makeStyles } from "@material-ui/core";
import * as React from "react";

import type { AgentSupportedPropertyTypeEnum } from "@/api/generated";
import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import theme from "@/theme";

// 対応可能種別と得意な領域のタグ（Figma B-1 案A）。
// 種別は 3 値固定なので種別ごとに色を固定し、どの画面でも同じ色で読めるようにする。
// 得意な領域は会社ごとに件数が変わる説明なので無彩色の面にし、種別より目立たせない。
// どちらもタップできないので角丸は radius/sm（ピルはタップできる Chip 専用）。
const useStyles = makeStyles(() => ({
  list: { display: "flex", flexWrap: "wrap", gap: layoutTokens.space[8] },
  type: {
    ...textStyles["SP/Heading/H4"],
    lineHeight: 1.5,
    display: "inline-flex",
    alignItems: "center",
    padding: `${layoutTokens.space[4]}px ${layoutTokens.space[12]}px`,
    borderRadius: layoutTokens.radius.sm,
    color: semanticColors["text/inverse"],
    whiteSpace: "nowrap",
  },
  typeCondo: { backgroundColor: semanticColors["category/condo"] },
  typeBuilding: { backgroundColor: semanticColors["category/building"] },
  typeOverseas: { backgroundColor: semanticColors["category/overseas"] },
  // 「確認必要」（未設定）は種別の色と混ざらないよう白地に線だけ
  typeUnknown: {
    color: semanticColors["text/secondary"],
    backgroundColor: semanticColors["surface/card"],
    boxShadow: `inset 0 0 0 1px ${semanticColors["border/default"]}`,
  },
  strength: {
    ...textStyles["SP/Body/Small"],
    display: "inline-flex",
    alignItems: "center",
    padding: `${layoutTokens.space[4]}px ${layoutTokens.space[12]}px`,
    borderRadius: layoutTokens.radius.sm,
    backgroundColor: semanticColors["tag/strength-bg"],
    color: semanticColors["text/primary"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { fontSize: textStyles["PC/Body/Small"].fontSize },
  },
}));

// 種別が増減したら型エラーで気づけるよう enum をキーにする（値の import は生成クライアント経由で axios を読むので型だけ使う）
const TYPE_CLASS_KEYS: Record<AgentSupportedPropertyTypeEnum, "typeCondo" | "typeBuilding" | "typeOverseas"> = {
  区分: "typeCondo",
  一棟: "typeBuilding",
  海外不動産: "typeOverseas",
};

const typeClassName = (classes: ReturnType<typeof useStyles>, label: string): string => {
  const key = TYPE_CLASS_KEYS[label as AgentSupportedPropertyTypeEnum];
  return key ? classes[key] : classes.typeUnknown;
};

/** 対応可能種別 1 件。label は buildSupportedPropertyTypeLabels の結果（種別名か「確認必要」） */
export const PropertyTypeTag: React.FC<{ label: string; className?: string }> = ({ label, className }) => {
  const classes = useStyles();
  return (
    <span
      className={`${classes.type} ${typeClassName(classes, label)} ${className ?? ""}`}
      data-testid="property-type-tag"
      data-type={label}
    >
      {label}
    </span>
  );
};

/** 得意な領域 1 件（会社の検索タグ名） */
export const StrengthTag: React.FC<{ label: string; className?: string }> = ({ label, className }) => {
  const classes = useStyles();
  return (
    <span className={`${classes.strength} ${className ?? ""}`} data-testid="strength-tag">
      {label}
    </span>
  );
};

export const PropertyTypeTags: React.FC<{ labels: readonly string[]; className?: string }> = ({ labels, className }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.list} ${className ?? ""}`} data-testid="property-type-tags">
      {labels.map((label) => (
        <PropertyTypeTag key={label} label={label} />
      ))}
    </Box>
  );
};

export const StrengthTags: React.FC<{ labels: readonly string[]; className?: string }> = ({ labels, className }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.list} ${className ?? ""}`} data-testid="strength-tags">
      {labels.map((label, index) => (
        <StrengthTag key={`${label}-${index}`} label={label} />
      ))}
    </Box>
  );
};
